import { useState } from 'react';

import type { TenantAdminSummaryResponse } from '../api';
import { useLicensePlans } from '../hooks/useLicensePlans';

interface ChangeLicenseModalProps {
  tenant: TenantAdminSummaryResponse | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (newLicense: string, expiresAt: string) => void;
}

export function ChangeLicenseModal({ tenant, busy, onClose, onSubmit }: ChangeLicenseModalProps): JSX.Element | null {
  const { data: plans } = useLicensePlans();
  const [newLicense, setNewLicense] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');

  if (tenant === null) return null;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (newLicense === '' || expiresAt === '') return;
    onSubmit(newLicense, new Date(expiresAt).toISOString());
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-license-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <h2 id="change-license-title" className="text-lg font-semibold text-slate-900">
          Cambiar plan
        </h2>
        <p className="mt-1 text-sm text-slate-500">{tenant.name}</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label" htmlFor="new-license">Nuevo plan</label>
            <select
              id="new-license"
              className="input"
              value={newLicense}
              onChange={(e) => setNewLicense(e.target.value)}
              disabled={busy}
              required
            >
              <option value="">Seleccionar plan…</option>
              {(plans ?? []).map((p) => (
                <option key={p.licenseType} value={p.licenseType}>{p.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="license-expires">Fecha de expiración</label>
            <input
              id="license-expires"
              type="date"
              className="input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={busy}
              required
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={busy || newLicense === '' || expiresAt === ''}>
            {busy ? 'Guardando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </div>
  );
}
